import { useAuth } from '@clerk/clerk-react';
import type { AppType } from '@lander-mgr/worker/routers/dashboard';
import { hc } from 'hono/client';
import { useTenantContext } from './auth';

// Typed Hono RPC client for the dashboard (Task 25 / MIS-806).
//
// `hc<AppType>` infers every path/method/body/response from the worker's
// dashboard router (`AppType = typeof routes`), so callers get full end-to-end
// type-safety with no `any`: e.g. `const api = useApi(); api.api.domains.$get()`.
//
// AppType is imported DIRECTLY from the worker package's `exports`
// (`@lander-mgr/worker/routers/dashboard`), NOT from `@lander-mgr/shared` —
// re-exporting it through shared would create a circular composite reference
// (shared is a dependency of the worker). See packages/shared/src/index.ts.
//
// The base path is `/` (the routes are mounted at `/api/*` on the server, so the
// typed call is `client.api.domains.$get`, not `client.domains.$get`). It can be
// overridden at build time via `VITE_API_BASE` for proxying scenarios.

/**
 * The typed dashboard RPC client. Aliased as a generic-instantiation reference
 * (NOT left to inference): `composite: true` forces declaration emit for this
 * file, and the fully-expanded `hc<AppType>` structure exceeds tsc's
 * serialization limit (TS7056) now that the dashboard route chain has grown
 * (FF-8/9/14…). The alias serializes compactly; consumers' `ReturnType<typeof
 * useApi>` keeps working unchanged.
 */
export type ApiClient = ReturnType<typeof hc<AppType>>;

/**
 * Hook returning a per-render RPC client that injects the current Clerk session
 * token and the active tenant on every request.
 *
 * - `Authorization: Bearer <token>` from `useAuth().getToken()` — fetched fresh
 *   per request (never cached in module scope) so tokens stay valid.
 * - `X-Tenant-Id` from `useTenantContext().activeTenantId`, when set.
 * - Headers are REPLACED (not merged) so a caller's stray auth header can't
 *   shadow the injected one; method/body and other init fields are preserved.
 */
export function useApi(): ApiClient {
  const { getToken } = useAuth();
  const { activeTenantId } = useTenantContext();

  return hc<AppType>(import.meta.env.VITE_API_BASE ?? '/', {
    fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
      const token = await getToken();
      const headers = new Headers(init?.headers);

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      if (activeTenantId) {
        headers.set('X-Tenant-Id', activeTenantId);
      }

      return fetch(input, { ...init, headers });
    },
  });
}
